import React, { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMemberItem } from '../../services/affiliateService'
import { affiliateTopRankClass } from './affiliateUi'
import './affiliateLayout.css'

const TOP_TEN_LIMIT = 10

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return Number.isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateToptenPage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list.slice(0, TOP_TEN_LIMIT))
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="htd-aff__loading">
        <div className="spinner-border" role="status" />
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="htd-aff__shell">
        <h3>โหลดข้อมูลไม่สำเร็จ</h3>
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="htd-aff__stack">
      <section className="htd-aff__panel">
        <div className="htd-aff__panel-head">
          <div className="d-flex align-items-center gap-2">
            <span className="htd-aff__head-icon htd-aff__head-icon--gold" aria-hidden>
              <i className="fas fa-trophy" />
            </span>
            <div>
              <h3>Top 10 สมาชิก</h3>
              <p>อันดับสมาชิกในเครือข่ายที่สมัครล่าสุด</p>
            </div>
          </div>
        </div>
      </section>

      {members.length === 0 ? (
        <div className="htd-aff__empty">
          <i className="fas fa-trophy" />
          <h4>ไม่มีรายการ</h4>
        </div>
      ) : (
        <div className="htd-aff__grid-2">
          {members.map((member, index) => (
            <article key={`topten-${member.username}-${index}`} className="htd-aff__member-card">
              <div className="htd-aff__member-top">
                <h3>{maskMember(member.username || member.displayName || 'สมาชิก')}</h3>
                <span className={affiliateTopRankClass(index)}>
                  {index < 3 ? (
                    <>
                      <i className="fas fa-medal me-1" />
                      #{index + 1}
                    </>
                  ) : (
                    <>#{index + 1}</>
                  )}
                </span>
              </div>
              <div className="htd-aff__meta">
                สมัครเมื่อวันที่ {formatJoinDate(member.joinedAt)}{' '}
                <span className="htd-aff__status">{member.status || 'สมาชิกปกติ'}</span>
              </div>
              <div className="htd-aff__metrics">
                <div className="htd-aff__metric">
                  <span>เดิมพันทั้งหมด</span>
                  <strong>{walletService.formatCurrency(0)}</strong>
                </div>
                <div className="htd-aff__metric htd-aff__metric--muted">
                  <span>คอมมิชชั่น</span>
                  <strong>{walletService.formatCurrency(0)}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}

export default AffiliateToptenPage
